import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, Plus, Trash, FilePdf, CaretDown, CaretUp, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ModuleGuide, EmptyState, LightChecklist } from '../components/GuidanceComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  Draft: 'bg-zinc-100 text-zinc-700 border-zinc-300',
  Issued: 'bg-blue-50 text-blue-700 border-blue-300',
  'In Progress': 'bg-amber-50 text-amber-700 border-amber-300',
  Completed: 'bg-green-50 text-green-700 border-green-300',
};

const STATUS_FLOW = ['Draft', 'Issued', 'In Progress', 'Completed'];

export const WorkOrders = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [workOrders, setWorkOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [trades, setTrades] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedWO, setExpandedWO] = useState(null);

  const [form, setForm] = useState({
    vendor_id: '', quote_id: '', trade_id: '', scope_of_work: '',
    start_date: '', end_date: '', payment_terms: '', notes: '',
    terms_and_conditions: '',
  });
  const [lineItems, setLineItems] = useState([{ description: '', unit: '', quantity: 0, rate: 0 }]);

  const fetchData = useCallback(async () => {
    try {
      const [woRes, vendorRes, tradeRes, quoteRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/work-orders`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/vendors?entity_type=vendor`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/trades`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/quotes`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setWorkOrders(woRes.data);
      setVendors(vendorRes.data);
      setTrades(tradeRes.data);
      setQuotes(quoteRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ vendor_id: '', quote_id: '', trade_id: '', scope_of_work: '', start_date: '', end_date: '', payment_terms: '', notes: '', terms_and_conditions: '' });
    setLineItems([{ description: '', unit: '', quantity: 0, rate: 0 }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vendor_id) { toast.error('Select a vendor'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/projects/${projectId}/work-orders`, {
        ...form, project_id: projectId, line_items: lineItems,
      }, { withCredentials: true });
      toast.success('Work Order created');
      setDialogOpen(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create WO'); }
  };

  const handleStatusChange = async (woId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/work-orders/${woId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
  };

  const handleDelete = async (woId) => {
    if (!window.confirm('Delete this Work Order?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/work-orders/${woId}`, { withCredentials: true });
      toast.success('Work Order deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleExportPdf = async (woId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/work-orders/${woId}/export/pdf`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `WO-${woId}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
    } catch { toast.error('Failed to export PDF'); }
  };

  const addLineItem = () => setLineItems([...lineItems, { description: '', unit: '', quantity: 0, rate: 0 }]);
  const removeLineItem = (idx) => setLineItems(lineItems.filter((_, i) => i !== idx));
  const updateLineItem = (idx, field, value) => {
    const updated = [...lineItems];
    updated[idx][field] = value;
    setLineItems(updated);
  };

  const lineItemsTotal = lineItems.reduce((s, li) => s + (parseFloat(li.quantity) || 0) * (parseFloat(li.rate) || 0), 0);
  const totalWoValue = workOrders.reduce((s, wo) => s + wo.total_amount, 0);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-zinc-600">Loading...</div></div>;

  return (
    <div>
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button onClick={() => navigate(`/projects/${projectId}?tab=control`)} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3" data-testid="back-button">
            <ArrowLeft size={18} className="mr-1.5" /> Back to Control Tower
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900" data-testid="wo-page-title">Work Orders</h1>
        </div>
      </header>
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ModuleGuide text="Issue Work Orders to subcontractors and vendors for trade-wise execution. Track progress from Draft to Completion." />

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-zinc-200 p-4"><span className="text-xs font-bold uppercase text-zinc-500">Total WOs</span><p className="text-2xl font-bold text-zinc-900 mt-1" data-testid="wo-count">{workOrders.length}</p></div>
          <div className="bg-white border border-zinc-200 p-4"><span className="text-xs font-bold uppercase text-zinc-500">Total Value</span><p className="text-2xl font-bold text-blue-700 mt-1">₹{totalWoValue.toLocaleString('en-IN')}</p></div>
          <div className="bg-white border border-zinc-200 p-4"><span className="text-xs font-bold uppercase text-zinc-500">Active</span><p className="text-2xl font-bold text-amber-600 mt-1">{workOrders.filter(w => w.status === 'In Progress').length}</p></div>
        </div>

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-zinc-800">All Work Orders</h2>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="create-wo-button">
                <Plus size={18} className="mr-1.5" /> Create Work Order
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-xl font-bold">New Work Order</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase text-zinc-500">Vendor *</Label>
                    <select value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} required
                      className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="wo-vendor-select">
                      <option value="">Select Vendor</option>
                      {vendors.map(v => <option key={v.id} value={v.id}>{v.legal_name} ({v.code})</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-zinc-500">Trade</Label>
                    <select value={form.trade_id} onChange={(e) => setForm({ ...form, trade_id: e.target.value })}
                      className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="wo-trade-select">
                      <option value="">Select Trade</option>
                      {trades.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase text-zinc-500">Linked Quote</Label>
                  <select value={form.quote_id} onChange={(e) => setForm({ ...form, quote_id: e.target.value })}
                    className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="wo-quote-select">
                    <option value="">Select Quote</option>
                    {quotes.map(q => <option key={q.id} value={q.id}>{q.pi_serial || q.id} — {q.status}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase text-zinc-500">Scope of Work *</Label>
                  <textarea value={form.scope_of_work} onChange={(e) => setForm({ ...form, scope_of_work: e.target.value })} required
                    rows={3} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="wo-scope" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs font-bold uppercase text-zinc-500">Start Date</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 rounded-none" data-testid="wo-start" /></div>
                  <div><Label className="text-xs font-bold uppercase text-zinc-500">End Date</Label>
                    <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 rounded-none" data-testid="wo-end" /></div>
                </div>

                {/* Line Items */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-bold uppercase text-zinc-500">Line Items</Label>
                    <Button type="button" onClick={addLineItem} variant="outline" size="sm" className="rounded-none border-zinc-300 h-7 text-xs" data-testid="wo-add-item">
                      <Plus size={14} className="mr-1" /> Add Item
                    </Button>
                  </div>
                  {lineItems.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end">
                      <div className="col-span-4"><Input placeholder="Description" value={item.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)} className="rounded-none text-sm" /></div>
                      <div className="col-span-2"><Input placeholder="Unit" value={item.unit} onChange={(e) => updateLineItem(idx, 'unit', e.target.value)} className="rounded-none text-sm" /></div>
                      <div className="col-span-2"><Input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)} className="rounded-none text-sm" /></div>
                      <div className="col-span-2"><Input type="number" placeholder="Rate" value={item.rate} onChange={(e) => updateLineItem(idx, 'rate', e.target.value)} className="rounded-none text-sm" /></div>
                      <div className="col-span-1 text-xs text-right pt-2 font-medium">₹{((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)).toLocaleString('en-IN')}</div>
                      <div className="col-span-1"><Button type="button" onClick={() => removeLineItem(idx)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-600 h-9 px-2"><Trash size={14} /></Button></div>
                    </div>
                  ))}
                  <p className="text-sm font-bold text-right mt-1">Total: ₹{lineItemsTotal.toLocaleString('en-IN')}</p>
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase text-zinc-500">Payment Terms</Label>
                  <textarea value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                    rows={2} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="wo-payment" placeholder="e.g., 30% advance, 40% on completion, 30% after defect liability" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase text-zinc-500">Notes / Special Instructions</Label>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={2} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="wo-notes" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase text-zinc-500">Terms & Conditions (editable)</Label>
                  <textarea value={form.terms_and_conditions} onChange={(e) => setForm({ ...form, terms_and_conditions: e.target.value })}
                    rows={4} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="wo-terms"
                    placeholder="Standard T&C will be applied if left blank" />
                </div>

                <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-5" data-testid="wo-submit">Create Work Order</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Work Order List */}
        {workOrders.length === 0 ? (
          <EmptyState
            title="No Work Orders created yet"
            description="Create a Work Order once a trade is subcontracted. Each WO links to a vendor and defines the scope, timeline, and payment terms."
            testId="wo-empty-state"
          />
        ) : (
          <div className="space-y-3">
            {workOrders.map(wo => {
              const isExpanded = expandedWO === wo.id;
              return (
                <div key={wo.id} className="bg-white border border-zinc-200" data-testid={`wo-card-${wo.id}`}>
                  <div className="p-4 flex items-start justify-between cursor-pointer" onClick={() => setExpandedWO(isExpanded ? null : wo.id)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs font-mono bg-zinc-100 text-zinc-600 px-2 py-0.5 border border-zinc-200" data-testid={`wo-number-${wo.id}`}>{wo.wo_number}</span>
                        <span className={`text-xs px-2 py-0.5 border ${STATUS_COLORS[wo.status]}`}>{wo.status}</span>
                        <span className="text-sm font-semibold text-zinc-800">{wo.vendor_name}</span>
                        {wo.trade_name && <span className="text-xs text-zinc-500">({wo.trade_name})</span>}
                      </div>
                      <p className="text-xs text-zinc-500 mt-1 line-clamp-1">{wo.scope_of_work}</p>
                      <div className="flex gap-4 mt-1 text-xs text-zinc-500">
                        <span>₹{wo.total_amount.toLocaleString('en-IN')}</span>
                        {wo.start_date && <span>{wo.start_date} → {wo.end_date || 'TBD'}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button onClick={(e) => { e.stopPropagation(); handleExportPdf(wo.id); }} variant="outline" size="sm" className="rounded-none border-zinc-300 h-7 text-xs" data-testid={`wo-pdf-${wo.id}`}>
                        <FilePdf size={14} className="mr-1" /> PDF
                      </Button>
                      {isExpanded ? <CaretUp size={18} className="text-zinc-400" /> : <CaretDown size={18} className="text-zinc-400" />}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-zinc-200 p-4 bg-zinc-50 space-y-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <div><span className="font-bold uppercase text-zinc-500">Vendor Code</span><p className="text-zinc-800 mt-0.5">{wo.vendor_code}</p></div>
                        <div><span className="font-bold uppercase text-zinc-500">Trade</span><p className="text-zinc-800 mt-0.5">{wo.trade_name || 'N/A'}</p></div>
                        <div><span className="font-bold uppercase text-zinc-500">Timeline</span><p className="text-zinc-800 mt-0.5">{wo.start_date || 'TBD'} → {wo.end_date || 'TBD'}</p></div>
                        <div><span className="font-bold uppercase text-zinc-500">Created</span><p className="text-zinc-800 mt-0.5">{new Date(wo.created_at).toLocaleDateString()}</p></div>
                      </div>
                      <div><h4 className="text-xs font-bold uppercase text-zinc-500 mb-1">Scope</h4><p className="text-sm text-zinc-700 bg-white border border-zinc-200 p-3">{wo.scope_of_work}</p></div>
                      {wo.line_items.length > 0 && (
                        <div>
                          <h4 className="text-xs font-bold uppercase text-zinc-500 mb-1">Line Items</h4>
                          <table className="w-full text-xs border-collapse bg-white border border-zinc-200">
                            <thead className="bg-zinc-100"><tr>{['Description', 'Unit', 'Qty', 'Rate', 'Amount'].map(h => <th key={h} className="px-3 py-2 text-left font-bold uppercase text-zinc-500 border border-zinc-200">{h}</th>)}</tr></thead>
                            <tbody>
                              {wo.line_items.map((li, i) => (
                                <tr key={i} className="border border-zinc-200">
                                  <td className="px-3 py-2">{li.description}</td><td className="px-3 py-2">{li.unit}</td>
                                  <td className="px-3 py-2">{li.quantity}</td><td className="px-3 py-2">₹{parseFloat(li.rate || 0).toLocaleString('en-IN')}</td>
                                  <td className="px-3 py-2 font-medium">₹{(parseFloat(li.quantity || 0) * parseFloat(li.rate || 0)).toLocaleString('en-IN')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {wo.payment_terms && <div><h4 className="text-xs font-bold uppercase text-zinc-500 mb-1">Payment Terms</h4><p className="text-xs text-zinc-700">{wo.payment_terms}</p></div>}
                      {wo.notes && <div><h4 className="text-xs font-bold uppercase text-zinc-500 mb-1">Notes</h4><p className="text-xs text-zinc-700">{wo.notes}</p></div>}
                      <div className="flex items-center gap-2 pt-2 border-t border-zinc-200">
                        <Label className="text-xs font-bold uppercase text-zinc-500">Status:</Label>
                        <select value={wo.status} onChange={(e) => handleStatusChange(wo.id, e.target.value)}
                          className="bg-white border border-zinc-200 rounded-none p-1.5 text-xs" data-testid={`wo-status-${wo.id}`}>
                          {STATUS_FLOW.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {wo.status === 'Draft' && (
                          <Button onClick={() => handleDelete(wo.id)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-600 h-7 text-xs ml-auto" data-testid={`wo-delete-${wo.id}`}>
                            <Trash size={14} className="mr-1" /> Delete
                          </Button>
                        )}
                      </div>
                      {wo.status === 'In Progress' && (
                        <LightChecklist
                          title="Before marking Work Order as Complete"
                          items={['Work verified on site', 'Snags / defects cleared', 'Measurements confirmed']}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default WorkOrders;
